/**
 * Card image gallery for collection and search cards.
 *
 * Photos 3 and 4 ship as data-srcset and are promoted to a real srcset by hydrate() on
 * the first sign of interest: hover, touch, keyboard focus, a swipe of the track, or an
 * arrow press. Shipping them with a real src cost a measured 364 kB and 1,926 ms of LCP
 * on a twelve card page, because loading="lazy" does not defer a slide clipped inside an
 * overflow container, and on a collection page the LCP element is one of these photos.
 * Hydration happens on intent rather than on paint, so the photo is already arriving
 * before the shopper can reach the second slide.
 *
 * The arrows scroll the track, the same mechanism a swipe uses on a touch device.
 * Two things this must not do, both measured on the live store:
 *   - never pass behavior 'smooth': Chrome clamps a negative scrollLeft to 0 on every
 *     smooth path, and an RTL scroll offset is negative, so the move silently does nothing
 *   - never move the track with a transform: the track is the element carrying
 *     overflow hidden, so a transform carries its clipping window along with its content
 *     and the photo ends up outside the clip of .media, which shows an empty card
 * The step is a delta measured from the live boxes, so RTL and LTR both work without
 * assuming which end scrollLeft counts from.
 */
if (!customElements.get('card-gallery')) {
  class CardGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.track = this.querySelector('.card-gallery__track');
      this.slides = Array.from(this.querySelectorAll('.card-gallery__slide'));
      this.dots = Array.from(this.querySelectorAll('.card-gallery__dot'));
      this.prev = this.querySelector('.card-gallery__arrow--prev');
      this.next = this.querySelector('.card-gallery__arrow--next');
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.frame = null;
      this.hydrated = false;

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

      // the first sign that this shopper wants more than photo 1
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
        this.addEventListener(type, () => this.hydrate(), { once: true, passive: true });
      });
      this.track.addEventListener('scroll', () => this.hydrate(), { once: true, passive: true });

      // the whole card is a link that LinkArea opens on mouseup, so an arrow click has
      // to be stopped before it bubbles or the arrow would open the product page
      [this.prev, this.next].forEach((button, position) => {
        if (!button) return;
        ['mousedown', 'mouseup', 'click'].forEach((type) => {
          button.addEventListener(type, (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hydrate();
            if (type === 'click') this.go(this.index + (position === 0 ? -1 : 1));
          });
        });
      });

      // choosing another colourway rewrites photo 1 in place, which would leave photos
      // 3 and 4 belonging to the product the shopper just moved away from
      this.closest('product-card')?.addEventListener('activeSibling', () => this.collapse());

      this.update();
    }

    // Give the held back slides their urls. Idempotent, and safe to call from several
    // listeners at once. fetchpriority high because the shopper is waiting on this photo
    // now, which is the opposite of the situation it ships in.
    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      this.querySelectorAll('img[data-srcset]').forEach((image) => {
        image.fetchPriority = 'high';
        image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
      });
    }

    go(index) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      const target = this.slides[clamped];
      if (!target || clamped === this.index) return;
      const delta = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollLeft = this.track.scrollLeft + delta;
      this.index = clamped;
      this.update();
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        const middle = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
        let closest = 0;
        let shortest = Infinity;
        this.slides.forEach((slide, i) => {
          const box = slide.getBoundingClientRect();
          const distance = Math.abs(box.left + box.width / 2 - middle);
          if (distance < shortest) {
            shortest = distance;
            closest = i;
          }
        });
        if (closest !== this.index) {
          this.index = closest;
          this.update();
        }
      });
    }

    update() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('card-gallery__dot--active', i === this.index);
      });
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    }

    collapse() {
      this.slides.slice(1).forEach((slide) => slide.remove());
      this.querySelector('.card-gallery__dots')?.remove();
      this.prev?.remove();
      this.next?.remove();
      this.slides = this.slides.slice(0, 1);
      this.dots = [];
      this.prev = null;
      this.next = null;
      this.index = 0;
      this.track.scrollLeft = 0;
    }
  }

  customElements.define('card-gallery', CardGallery);
}
